import cx from 'classnames';
import {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {CardEditorDesktop} from '~/common/FabricInvitation/EventCreateDesign/components/CardEditor/Desktop/CardEditorDesktop';
import {CardEditorMobile} from '~/common/FabricInvitation/EventCreateDesign/components/CardEditor/Mobile/CardEditorMobile';
import {TextEditor as MobileTextEditor} from '~/common/FabricInvitation/EventCreateDesign/components/CardEditor/Mobile/TextEditor/TextEditor';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {
  selectActiveCardEditor,
  selectEditingMobile,
  selectEditorDisabled,
  selectTouched,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {ImageEditor} from './ImageEditor/ImageEditor';
import {Instructions} from './Instructions/Instructions';
import './EventCreateEditor.sass';
import {triggerSelectFirstTextbox} from '~/common/canvas/utils/textboxUtils';
import {triggerSelectFirstUyo} from '~/common/canvas/utils/uyoUtils';

const EDITORS = {
  '': Instructions,
  intro: Instructions,
  image: ImageEditor,
  text: CardEditorDesktop,
};

const useCanvasActiveObject = (
  canvas,
  touched,
  dispatch,
  recentlyUsedFonts,
  setRecentlyUsedFonts
) => {
  useEffect(() => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject && !touched) {
      dispatch({type: UPDATE_DESIGN_OPTIONS, payload: {touched: true}});
    }
    if (
      activeObject &&
      activeObject.type === 'textbox' &&
      activeObject.fontFamily !== recentlyUsedFonts[0]?.family
    ) {
      setRecentlyUsedFonts((prev) => {
        const newFontFamily = activeObject.fontFamily || '';
        const newFontStyle = activeObject.fontStyle || '';
        const newFontWeight = activeObject.fontWeight || 'normal';
        const recentlyUsedFonts = prev.filter((font) => {
          return (
            font.family !== newFontFamily ||
            font.style !== newFontStyle ||
            font.weight !== newFontWeight
          );
        });
        return [
          {
            family: activeObject.fontFamily,
            name: activeObject.fontFamily,
            style: activeObject.fontStyle,
            weight: activeObject.fontWeight,
          },
          ...recentlyUsedFonts,
        ].slice(0, 3);
      });
    }
    return () => {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {textAreaOpen: false},
      });
    };
  });
};

const useUyoDetection = () =>
  useSelector((state) => {
    const fabricObjects = state?.fabricDesign?.fabricArt?.objects;
    return Boolean(fabricObjects?.some((obj) => obj.type === 'uYOImage'));
  });

export const EventCreateEditor = ({children}) => {
  const {canvas} = useContext(EventCreateContext);
  const dispatch = useDispatch();

  const isDesktop = useIsLargishAndUp();
  const activeCardEditor = useSelector(selectActiveCardEditor);
  const editingMobile = useSelector(selectEditingMobile);
  const touched = useSelector(selectTouched);
  const editorDisabled = useSelector(selectEditorDisabled);
  const isUyo = useUyoDetection();
  const [recentlyUsedFonts, setRecentlyUsedFonts] = useState([]);

  const showActiveEditor =
    isUyo || (!['', 'intro'].includes(activeCardEditor) && touched && !editorDisabled);
  const ActiveEditor = EDITORS[isUyo && editorDisabled ? '' : activeCardEditor];

  const handleActiveObjectSelection = useCallback(() => {
    const activeObject = canvas?.getActiveObject();
    if (activeCardEditor === 'image' && activeObject?.type !== 'uYOImage') {
      triggerSelectFirstUyo(canvas);
    } else if (activeCardEditor === 'text' && activeObject?.type !== 'textbox') {
      triggerSelectFirstTextbox(canvas);
    }
  }, [activeCardEditor, canvas]);

  useEffect(() => {
    if (!isDesktop) return;
    handleActiveObjectSelection();
  }, [isDesktop, handleActiveObjectSelection]);

  useEffect(() => {
    if (isDesktop || !editingMobile) return;
    handleActiveObjectSelection();
  }, [isDesktop, editingMobile, handleActiveObjectSelection]);

  useCanvasActiveObject(canvas, touched, dispatch, recentlyUsedFonts, setRecentlyUsedFonts);

  const editorClasses = cx('event-create-editor', {
    'event-create-editor--card-design': showActiveEditor,
    'event-create-editor--no-active-editor': !showActiveEditor,
  });

  return (
    <div className={editorClasses}>
      {children}
      <MobileTextEditor />
      {showActiveEditor && isDesktop && <ActiveEditor />}
      <CardEditorMobile
        recentlyUsedFonts={recentlyUsedFonts}
        setRecentlyUsedFonts={setRecentlyUsedFonts}
      />
    </div>
  );
};
